import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { CATEGORY_FOLD_COLOR, formatTry } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Name = styled.div`
  width: 110px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Track = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #EEF1F5;
  overflow: hidden;
`;

const Fill = styled.div`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  border-radius: 4px;
  background: ${({ $color }) => $color};
`;

const Amount = styled.div`
  width: 90px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: #52514e;
`;

const Spark = styled.svg`
  width: 64px;
  height: 20px;
  flex-shrink: 0;
`;

function Sparkline({ points, color }) {
  if (!points || points.length < 2) return <Spark />;
  const max = Math.max(...points, 1);
  const step = 64 / (points.length - 1);
  const d = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(18 - (v / max) * 16).toFixed(1)}`)
    .join(' ');
  return (
    <Spark viewBox="0 0 64 20" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" />
    </Spark>
  );
}

export default function CategoryTrendsCard({ slice, categoryColors }) {
  const { t } = useTranslation();
  const data = slice.data;

  const model = useMemo(() => {
    if (!data) return [];
    const months = [...new Set((data.monthlyMatrix || []).map((r) => r.monthStart))].sort();
    const max = Math.max(1, ...(data.currentMonth || []).map((c) => c.total));
    return (data.currentMonth || []).map((c) => ({
      ...c,
      pct: Math.round((c.total / max) * 100),
      color: categoryColors.get(c.categoryId) || CATEGORY_FOLD_COLOR,
      spark: months.map((m) => (data.monthlyMatrix.find((r) => r.monthStart === m && r.categoryId === c.categoryId)?.total) || 0),
    }));
  }, [data, categoryColors]);

  return (
    <DashboardCard
      cardKey="kategori-dagilimi"
      title={t('expenseDashboard.categories.title')}
      meta={t('expenseDashboard.categories.meta')}
      span={6}
      spanMd={6}
      loading={slice.loading}
      empty={model.length === 0}
      emptyText={t('expenseDashboard.categories.empty')}
    >
      <div>
        {model.map((c) => (
          <Row key={c.categoryId}>
            <Dot $color={c.color} />
            <Name title={c.categoryName}>{c.categoryName}</Name>
            <Track><Fill $pct={c.pct} $color={c.color} /></Track>
            <Amount>{formatTry(c.total)}</Amount>
            <Sparkline points={c.spark} color={c.color} />
          </Row>
        ))}
        {data?.nonTryCount > 0 && (
          <Row as="div" style={{ paddingTop: 4 }}>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
              {t('expenseDashboard.fxExcluded', { count: data.nonTryCount })}
            </span>
          </Row>
        )}
      </div>
    </DashboardCard>
  );
}
